import React, { useState, useCallback, useEffect } from 'react';
import { ProductDetail } from '../../../../types/ProductTypes';
import { SectionCard, SectionTitle, InfoGrid, InfoItem } from '../../Section.styles';
import { useDownloadFile } from '../../../../api/useDownloadFile';
import { useEditMode } from '../../../../context/EditModeContext';
import { useAuthStore } from '../../../../context/AuthContext';
import { useProductEdit } from '../../../../context/ProductEditContext';
import useGetProcedures from '../../../../api/useGetProcedures';
import {
    ProcedureCard,
    ProcedureTitle,
    ProcedureFileLink,
    ProcedureDescription,
    ProcedureComments,
    CommentsSection,
    CommentsLabel,
    CommentsText,
    FeedbackSection,
    FeedbackTitle,
    FeedbackDescription,
    FeedbackTextarea,
    FeedbackButton,
    NoProceduresMessage,
    EditSelect,
    EditTextarea,
    RemoveProcedureButton,
    ProcedureCardHeader,
    AddProcedureWrapper,
    CommentsEditSection,
} from './ProceduresSection.styles';

interface ProceduresSectionProps {
    product: ProductDetail;
}

const ProceduresSection: React.FC<ProceduresSectionProps> = ({ product }) => {
    const { downloadFile } = useDownloadFile();
    const { isInEditMode } = useEditMode();
    const { canEditChemGuard } = useAuthStore();
    const { mergePendingChanges } = useProductEdit();
    const [feedbackText, setFeedbackText] = useState('');

    // Local state for edit mode
    const [localSolubleInWater, setLocalSolubleInWater] = useState(0);
    const [localProcedures, setLocalProcedures] = useState<any[]>([]);
    const [selectedProcedureId, setSelectedProcedureId] = useState('');
    const [localCleaningComments, setLocalCleaningComments] = useState('');
    const [localCleaningCommentsMSG, setLocalCleaningCommentsMSG] = useState('');

    // Get all cleaning procedures from API (category 1)
    const { getProcedures, procedures: allCleaningProcedures } = useGetProcedures(1);

    const showEditUI = isInEditMode;

    // Initialize local state when entering edit mode
    useEffect(() => {
        if (showEditUI) {
            setLocalSolubleInWater(product.physicalData?.solubleInWater || 0);
            setLocalProcedures(product.procedures?.filter((p) => p.category === 1) || []);
            setLocalCleaningComments(product.comments?.find((c) => c.category === 9)?.text || '');
            setLocalCleaningCommentsMSG(product.comments?.find((c) => c.category === 15)?.text || '');
        }
    }, [showEditUI, product]);

    // Fetch procedures when component mounts
    useEffect(() => {
        getProcedures();
    }, [getProcedures]);

    // Filter for cleaning procedures (Category 1)
    const cleaningProcedures = showEditUI ? localProcedures : product.procedures?.filter((p) => p.category === 1) || [];

    // Get comments - Category 9 (Cleaning Procedure) and Category 15 (Cleaning Procedure MSG)
    const cleaningComments = product.comments?.find((c) => c.category === 9);
    const cleaningCommentsMSG = product.comments?.find((c) => c.category === 15);

    // Map solubility enum to string
    const getSolubilityText = (value: number): string => {
        switch (value) {
            case 0:
                return 'Unknown';
            case 1:
                return 'Yes';
            case 2:
                return 'No';
            case 3:
                return 'Partly';
            default:
                return 'Unknown';
        }
    };

    // Handler for Soluble in Water change
    const handleSolubleInWaterChange = useCallback(
        (value: number) => {
            setLocalSolubleInWater(value);
            if (mergePendingChanges) {
                mergePendingChanges({
                    physicalData: {
                        ...product.physicalData,
                        solubleInWater: value,
                    } as any,
                });
            }
        },
        [mergePendingChanges, product.physicalData]
    );

    // Handler for adding a procedure
    const handleAddProcedure = useCallback(
        (procedureId: string) => {
            if (!procedureId) return;

            const procedureToAdd = allCleaningProcedures.find((p: any) => p.id.toString() === procedureId);
            if (!procedureToAdd) return;

            // Check if procedure already exists
            if (localProcedures.some((p: any) => p.id === procedureToAdd.id)) {
                setSelectedProcedureId('');
                return;
            }

            const updatedProcedures = [...localProcedures, { ...procedureToAdd, category: 1 }];
            setLocalProcedures(updatedProcedures);
            setSelectedProcedureId('');

            if (mergePendingChanges) {
                mergePendingChanges({
                    procedures: updatedProcedures,
                });
            }
        },
        [allCleaningProcedures, localProcedures, mergePendingChanges]
    );

    // Handler for removing a procedure
    const handleRemoveProcedure = useCallback(
        (procedureId: number) => {
            const updatedProcedures = localProcedures.filter((p: any) => p.id !== procedureId);
            setLocalProcedures(updatedProcedures);

            if (mergePendingChanges) {
                mergePendingChanges({
                    procedures: updatedProcedures,
                });
            }
        },
        [localProcedures, mergePendingChanges]
    );

    // Handler for Cleaning Comments change
    const handleCleaningCommentsChange = useCallback(
        (value: string) => {
            setLocalCleaningComments(value);
            if (mergePendingChanges) {
                const existingComment = product.comments?.find((c) => c.category === 9);
                const updatedComment = {
                    id: existingComment?.id || 0,
                    text: value,
                    category: 9,
                    visibility: existingComment?.visibility || 0,
                    lastUpdatedUtc: new Date().toISOString(),
                };
                mergePendingChanges({
                    comments: [updatedComment],
                });
            }
        },
        [mergePendingChanges, product.comments]
    );

    // Handler for Cleaning Comments MSG change
    const handleCleaningCommentsMSGChange = useCallback(
        (value: string) => {
            setLocalCleaningCommentsMSG(value);
            if (mergePendingChanges) {
                const existingComment = product.comments?.find((c) => c.category === 15);
                const updatedComment = {
                    id: existingComment?.id || 0,
                    text: value,
                    category: 15,
                    visibility: existingComment?.visibility || 0,
                    lastUpdatedUtc: new Date().toISOString(),
                };
                mergePendingChanges({
                    comments: [updatedComment],
                });
            }
        },
        [mergePendingChanges, product.comments]
    );

    // Get tank cleaning safety procedure
    const getTankCleaningSafetyProcedure = (): string => {
        // Logic from old implementation - based on MarpolRegulation and VapourDetection
        const marpolRegulation = product.category?.regulations?.marpolRegulation;
        const ibcRegulation = product.category?.regulations?.ibcRegulation;
        // VapourDetection values: 1=Flammable, 2=Toxic, 3=Flammable & Toxic
        const vapourDetection = ibcRegulation?.vapourDetection;

        let result = '';
        
        if (marpolRegulation === 2 && vapourDetection === 1) {
            result = 'Owners procedure for ventilation and tank cleaning of flammable products shall be followed before a commercial cleaning as per guidance in the commodity book can commence.';
        } else if (marpolRegulation === 2 && vapourDetection === 2) {
            result = 'Owners procedure for ventilation and tank cleaning of toxic products shall be followed before a commercial cleaning as per guidance in the commodity book can commence.';
        } else if (marpolRegulation === 2 && vapourDetection === 3) {
            result = 'Owners procedure for ventilation and tank cleaning of flammable and toxic products shall be followed before a commercial cleaning as per guidance in the commodity book can commence.';
        } else if (marpolRegulation === 1) {
            result = "After unloading this Annex I product, ships shall follow owners' procedure for tank cleaning and ventilation of Annex I product. After aforementioned cleaning is completed and the tank is verified clean and gas free, commercial cleaning as per guidance in the commodity book can commence.";
        } else if (marpolRegulation === 3) {
            result = 'Owners procedure for ventilation and tank cleaning of flammable products shall be followed before a commercial cleaning as per guidance in the commodity book can commence';
        }

        return result;
    };

    const handleSendFeedback = () => {
        if (feedbackText.trim()) {
            // TODO: Implement feedback submission
            console.log('Feedback:', feedbackText);
            alert('Thank you for your feedback!');
            setFeedbackText('');
        }
    };

    return (
        <SectionCard>
            <SectionTitle>Cleaning Procedures</SectionTitle>

            {/* Soluble in Water */}
            <InfoItem>
                <label>Soluble in Water:</label>
                {showEditUI ? (
                    <EditSelect
                        value={localSolubleInWater}
                        onChange={(e) => handleSolubleInWaterChange(parseInt(e.target.value, 10))}
                        aria-label="Soluble in Water"
                        title="Soluble in Water"
                    >
                        <option value={0}>Unknown</option>
                        <option value={1}>Yes</option>
                        <option value={2}>No</option>
                        <option value={3}>Partly</option>
                    </EditSelect>
                ) : (
                    <span>{getSolubilityText(product.physicalData?.solubleInWater || 0)}</span>
                )}
            </InfoItem>
            <InfoItem>
                <label>Tank Cleaning Safety Procedure:</label>
                <span>{getTankCleaningSafetyProcedure()}</span>
            </InfoItem>

            {/* Cleaning Procedures List */}
            {cleaningProcedures.length === 0 && !showEditUI ? (
                <NoProceduresMessage>No cleaning procedures available.</NoProceduresMessage>
            ) : (
                cleaningProcedures.map((procedure) => (
                    <ProcedureCard key={procedure.id}>
                        <ProcedureCardHeader>
                            {showEditUI && (
                                <RemoveProcedureButton onClick={() => handleRemoveProcedure(procedure.id)} title="Remove procedure">
                                    🗑️
                                </RemoveProcedureButton>
                            )}
                            <ProcedureTitle>
                                {procedure.title} ({procedure.id})
                            </ProcedureTitle>
                        </ProcedureCardHeader>

                        {procedure.filePath && (
                            <ProcedureFileLink
                                href="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    downloadFile({ category: 'Procedure', fileName: procedure.filePath });
                                }}
                            >
                                {procedure.filePath}
                            </ProcedureFileLink>
                        )}

                        {procedure.description && (
                            <ProcedureDescription>
                                <pre>{procedure.description}</pre>
                            </ProcedureDescription>
                        )}

                        {procedure.comments && (
                            <ProcedureComments>
                                <label>Comments:</label>
                                <pre>{procedure.comments}</pre>
                            </ProcedureComments>
                        )}
                    </ProcedureCard>
                ))
            )}

            {/* Add Procedure Dropdown - Only shown in edit mode */}
            {showEditUI && (
                <AddProcedureWrapper>
                    <label>Select a procedure:</label>
                    <EditSelect
                        value={selectedProcedureId}
                        onChange={(e) => {
                            setSelectedProcedureId(e.target.value);
                            handleAddProcedure(e.target.value);
                        }}
                        aria-label="Add cleaning procedure"
                        title="Add cleaning procedure"
                    >
                        <option value="">Select a procedure...</option>
                        {allCleaningProcedures
                            .sort((a: any, b: any) => (a.title || a.Title || '').localeCompare(b.title || b.Title || ''))
                            .map((procedure: any) => (
                                <option key={procedure.id} value={procedure.id.toString()}>
                                    {procedure.title || procedure.Title} ({procedure.id})
                                </option>
                            ))}
                    </EditSelect>
                </AddProcedureWrapper>
            )}

            {/* Cleaning Procedure Comments (Category 9) */}
            {showEditUI ? (
                <CommentsEditSection>
                    <CommentsLabel>Comments:</CommentsLabel>
                    <EditTextarea
                        value={localCleaningComments}
                        onChange={(e) => handleCleaningCommentsChange(e.target.value)}
                        placeholder="Enter cleaning procedure comments..."
                        rows={3}
                    />
                </CommentsEditSection>
            ) : (
                cleaningComments &&
                cleaningComments.text && (
                    <CommentsSection>
                        <CommentsLabel>Comments:</CommentsLabel>
                        <CommentsText>{cleaningComments.text}</CommentsText>
                    </CommentsSection>
                )
            )}

            {/* Cleaning Procedure Comments MSG (Category 15) */}
            {showEditUI && canEditChemGuard ? (
                <CommentsEditSection>
                    <CommentsLabel>Comments (MSG):</CommentsLabel>
                    <EditTextarea
                        value={localCleaningCommentsMSG}
                        onChange={(e) => handleCleaningCommentsMSGChange(e.target.value)}
                        placeholder="Enter comments for MSG..."
                        rows={3}
                    />
                </CommentsEditSection>
            ) : (
                cleaningCommentsMSG &&
                cleaningCommentsMSG.text && (
                    <CommentsSection>
                        <CommentsLabel>Comments (MSG):</CommentsLabel>
                        <CommentsText>{cleaningCommentsMSG.text}</CommentsText>
                    </CommentsSection>
                )
            )}

            {/* Feedback Section - Hidden in edit mode */}
            {!showEditUI && (
                <FeedbackSection>
                    <FeedbackTitle>Help us improve Commodity Book</FeedbackTitle>
                    <FeedbackDescription>Feedback on cleaning procedure for this cargo</FeedbackDescription>
                    <FeedbackTextarea
                        value={feedbackText}
                        onChange={(e) => setFeedbackText(e.target.value)}
                        placeholder="Enter your feedback here..."
                        rows={4}
                    />
                    <FeedbackButton onClick={handleSendFeedback} disabled={!feedbackText.trim()}>
                        Send feedback
                    </FeedbackButton>
                </FeedbackSection>
            )}
        </SectionCard>
    );
};

export default ProceduresSection;
